'use client'
import cn from 'classnames'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { ExpertCard } from 'src/components/ExpertCard/ExpertCard'
import { HtmlContentViewer } from 'src/components/global/HtmlContentViewer/HtmlContentViewer'
import { PremiumTag } from 'src/components/global/PremiumTag/PremiumTag'
import { ThemeOnly } from 'src/components/global/ThemeOnly'
import { THEME } from 'src/config/config'
import { apolloSdk } from 'src/graphql/apolloSdk'
// import { apolloSdk } from 'src/graphql/apolloSdk'
import { ISubTopicFullItemFragment, ITopicFullItemFragment, useUserInfoLazyQuery } from 'src/graphql/generated/hooks'
import { AppState } from 'src/store/types'

import { FollowSubTopicButton } from '../global/FollowSubTopicButton'

interface TopicHeaderProps {
  className?: string
  topic: ITopicFullItemFragment | ISubTopicFullItemFragment
  subtopicButtonFlag?: boolean
}

/**
 * Primary UI component for user interaction
 */
export const TopicHeader: React.FC<TopicHeaderProps> = ({ className, topic, subtopicButtonFlag = false }) => {
  const expert = topic.attributes?.expert
  const isPremium = topic.attributes?.isPremium
  const [subTopic, setSubTopic] = useState<any>({})
  const authInfo = useSelector((state: AppState) => state.authInfo)
  const [loadUserInfo] = useUserInfoLazyQuery()
  const [userData, setUserData] = useState<any>()
  const [followed, setFollowed] = useState(false)

  const getUserData = async () => {
    try {
      const res = await loadUserInfo({
        variables: { token: authInfo.idToken || '' },
        fetchPolicy: 'no-cache',
      })
      const user = res?.data?.userInfo
      if (user) {
        setUserData(user)
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load user data', error)
    }
  }

  const fetchFollowedStatus = async (user: any) => {
    try {
      if (topic.attributes?.slug) {
        const data: any = await apolloSdk.getSubTopicBySlug({ slug: topic.attributes.slug })
        const subTopicsData = data?.subTopics?.data?.length > 0 ? data.subTopics.data[0] : null

        if (subtopicButtonFlag && subTopicsData && user?.email) {
          const followedUsers = subTopicsData.attributes?.followedUsers?.data || []
          const isFollowing = followedUsers.some((item: any) => item?.attributes?.email === user.email)
          setFollowed(isFollowing)
        } else {
          setFollowed(false)
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch follow status', error)
    }
  }

  useEffect(() => {
    if (subtopicButtonFlag && authInfo.idToken) {
      getUserData()
    }
  }, [authInfo.idToken])

  useEffect(() => {
    setFollowed(false)
    if (userData) {
      fetchFollowedStatus(userData)
    }
  }, [userData, topic?.attributes?.slug])

  useEffect(() => {
    setSubTopic(topic)
  }, [topic])

  return (
    <div className={cn(className, 'flex flex-row justify-center bg-clouds')}>
      <div
        className={cn(
          'px-5 md:pl-0 !max-w-[1100px] w-full flex flex-col md:flex-row items-center pt-2 pb-5 md:pb-0 topic-header',
          { 'ml-[40px]': THEME === 'ilta' },
        )}
      >
        <div className="flex flex-row">
          <div className="flex-4 md:pr-[7.5rem] mb-[2.125rem]">
            <div className="flex flex-row items-center">
              <h1 className="pt-[0.5rem] pb-[0.625rem] text-[1.875rem] font-bold font-secondary text-darkgray">
                {topic.attributes?.name}
              </h1>
              <ThemeOnly theme="legaltech">
                {isPremium && <PremiumTag className="ml-[10px] text-[0.8125rem]" />}
              </ThemeOnly>
            </div>
            {topic.attributes?.description && (
              <h2
                className={cn(
                  'text-[0.9375rem] font-primary font-regular text-darkgray',
                  subtopicButtonFlag && THEME === 'legaltech' ? 'mb-[16px]' : '',
                )}
              >
                <HtmlContentViewer
                  contentClassName={cn('font-primary', {
                    '!font-tertiary !font-regular !text-topicDescription': THEME === 'ilta',
                  })}
                  html={topic.attributes?.description}
                />
              </h2>
            )}
            <ThemeOnly theme="legaltech">
              {subtopicButtonFlag && (
                <div className="subtopic-button">
                  <FollowSubTopicButton
                    key={'flow-' + subTopic?.id + followed}
                    subTopic={subTopic}
                    id={''}
                    isButton={true}
                    displayName={null}
                    isEnhanced={false}
                    isFromSubTopic={true}
                    followed={followed}
                    setFollowed={setFollowed}
                  />
                </div>
              )}
            </ThemeOnly>
          </div>
        </div>
        <ThemeOnly theme="legaltech">{expert?.data && <ExpertCard expert={expert.data} />}</ThemeOnly>
      </div>
    </div>
  )
}
